import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/common';
import { Calendar } from '../components';
import { REGIONS, CONTACT_INFO, PRICING } from '../constants';
import { submitBooking } from '../services/api';
import type { BookingFormData } from '../types';
import { Button, Input, Select, Textarea } from '../components/ui';
import { createHibachiEvent, generateGoogleCalendarUrl, generateOutlookCalendarUrl, downloadICS } from '../utils';
import './BookNow.css';

interface TimeSlot {
	id: string;
	label: string;
	startTime: string;
	endTime: string;
	enabled: boolean;
}

/**
 * Book Now page component
 * Full booking form with region selection and calendar
 * Note: This is reservation only, no payment processing
 */
const BookNow: React.FC = () => {
	const { t, i18n } = useTranslation();
	const location = useLocation();
	const [selectedRegion, setSelectedRegion] = useState<string>('');
	const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
	const [couponsEnabled, setCouponsEnabled] = useState(true);
	const [formData, setFormData] = useState<BookingFormData>({
		name: '',
		email: '',
		phone: '',
		date: '',
		time: '',
		guestCount: 10,
		region: '',
		message: '',
		couponCode: '',
		referralSource: '',
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
	const [errorMessage, setErrorMessage] = useState<string>('');
	const [validationErrors, setValidationErrors] = useState<{
		email?: string;
		phone?: string;
	}>({});
	const [couponStatus, setCouponStatus] = useState<{
		valid: boolean;
		message: string;
		discount?: string;
	} | null>(null);
	const [checkingCoupon, setCheckingCoupon] = useState(false);

	// Fetch time slots and feature toggles from settings API
	useEffect(() => {
		const fetchTimeSlots = async () => {
			try {
				const response = await fetch('/api/settings');
				const result = await response.json();
				if (result.success && result.settings?.timeSlots) {
					setTimeSlots(result.settings.timeSlots.filter((s: TimeSlot) => s.enabled));
				}
				if (result.success && result.settings?.featureToggles) {
					setCouponsEnabled(result.settings.featureToggles.coupons !== false);
				}
			} catch (error) {
				console.error('Failed to fetch time slots:', error);
				// Fallback to default time slots - labels will be translated in the render
				setTimeSlots([
					{ id: 'afternoon', label: 'afternoon', startTime: '13:00', endTime: '15:00', enabled: true },
					{ id: 'evening', label: 'evening', startTime: '17:00', endTime: '19:00', enabled: true },
					{ id: 'night', label: 'night', startTime: '19:00', endTime: '21:00', enabled: true },
				]);
			}
		};
		fetchTimeSlots();
	}, []);

	// Validation functions
	const validateEmail = (email: string): boolean => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const validatePhone = (phone: string): boolean => {
		// Accept various phone formats: +1234567890, (123) 456-7890, 123-456-7890, etc.
		const phoneRegex = /^[\d\s\-+().]{10,}$/;
		return phoneRegex.test(phone.replace(/\s/g, ''));
	};

	// 验证优惠码
	const validateCoupon = async (code: string) => {
		if (!code.trim()) {
			setCouponStatus(null);
			return;
		}
		setCheckingCoupon(true);
		try {
			const response = await fetch(`/api/coupons?code=${encodeURIComponent(code.trim())}`);
			const result = await response.json();
			if (result.success && result.coupon) {
				const coupon = result.coupon;
				if (coupon.minGuests > 0 && formData.guestCount < coupon.minGuests) {
					setCouponStatus({
						valid: false,
						message: t('coupon.minGuests', { count: coupon.minGuests }) || `最少需要 ${coupon.minGuests} 人`,
					});
				} else {
					const discount = coupon.type === 'percentage' ? `${coupon.value}%` : `$${coupon.value}`;
					setCouponStatus({
						valid: true,
						message: t('coupon.applied', { discount }) || `优惠码有效！折扣: ${discount}`,
						discount,
					});
				}
			} else {
				setCouponStatus({
					valid: false,
					message: result.error || t('coupon.invalid', '优惠码无效'),
				});
			}
		} catch {
			setCouponStatus({
				valid: false,
				message: t('coupon.checkFailed', '验证失败'),
			});
		}
		setCheckingCoupon(false);
	};

	// Handle hash-based region selection
	useEffect(() => {
		const hash = location.hash.replace('#', '');
		if (hash && REGIONS.some((r) => r.id === hash)) {
			setSelectedRegion(hash);
			setFormData((prev) => ({ ...prev, region: hash }));

			// Scroll to the form
			setTimeout(() => {
				const formElement = document.getElementById('booking-form');
				if (formElement) {
					formElement.scrollIntoView({ behavior: 'smooth' });
				}
			}, 100);
		}
	}, [location.hash]);

	const handleRegionSelect = (regionId: string) => {
		setSelectedRegion(regionId);
		setFormData((prev) => ({ ...prev, region: regionId }));
	};

	const handleDateSelect = (date: string) => {
		setFormData((prev) => ({ ...prev, date }));
	};

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: name === 'guestCount' ? parseInt(value, 10) || 0 : value,
		}));

		// Real-time validation
		if (name === 'email') {
			if (value && !validateEmail(value)) {
				setValidationErrors((prev) => ({ ...prev, email: t('form.invalidEmail') as string }));
			} else {
				setValidationErrors((prev) => ({ ...prev, email: undefined }));
			}
		}

		if (name === 'phone') {
			if (value && !validatePhone(value)) {
				setValidationErrors((prev) => ({ ...prev, phone: t('form.invalidPhone') as string }));
			} else {
				setValidationErrors((prev) => ({ ...prev, phone: undefined }));
			}
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Final validation check
		const emailValid = validateEmail(formData.email);
		const phoneValid = validatePhone(formData.phone);

		if (!emailValid || !phoneValid) {
			setValidationErrors({
				email: !emailValid ? (t('form.invalidEmail') as string) : undefined,
				phone: !phoneValid ? (t('form.invalidPhone') as string) : undefined,
			});
			return;
		}

		setIsSubmitting(true);
		setErrorMessage('');

		try {
			const result = await submitBooking(formData);

			if (result.success) {
				setSubmitStatus('success');
			} else {
				setSubmitStatus('error');
				setErrorMessage(result.error || t('form.error') as string);
			}
		} catch (err) {
			setSubmitStatus('error');
			setErrorMessage(t('form.error') as string);
		}

		setIsSubmitting(false);
	};

	if (submitStatus === 'success') {
		const calendarEvent = createHibachiEvent(
			formData.date,
			formData.time,
			formData.guestCount,
			formData.region,
			formData.name
		);
		const googleCalUrl = generateGoogleCalendarUrl(calendarEvent);
		const outlookCalUrl = generateOutlookCalendarUrl(calendarEvent);

		return (
			<div className="book-now">
				<SEO title={t('nav.bookNow')} />
				<div className="booking-success">
					<div className="success-content">
						<div className="success-icon">🎉</div>
						<h2>{t('form.success')}</h2>
						<p>{t('form.successMessage')}</p>
						
						<div className="booking-summary">
							<h3>📋 {t('booking.summary', '预约详情')}</h3>
							<div className="summary-item">
								<span>📅 {t('form.date')}:</span>
								<strong>{new Date(formData.date + 'T00:00:00').toLocaleDateString(i18n.language, { 
									weekday: 'long', 
									year: 'numeric', 
									month: 'long', 
									day: 'numeric' 
								})}</strong>
							</div>
							<div className="summary-item">
								<span>⏰ {t('form.time')}:</span>
								<strong>{formData.time || t('common.toBeConfirmed', 'To be confirmed')}</strong>
							</div>
							<div className="summary-item">
								<span>👥 {t('form.guests')}:</span>
								<strong>{formData.guestCount} {t('form.people')}</strong>
							</div>
							<div className="summary-item">
								<span>📍 {t('form.region')}:</span>
								<strong>{formData.region.toUpperCase()}</strong>
							</div>
						</div>

						<div className="calendar-buttons">
							<h3>📆 {t('booking.addToCalendar', '添加到日历')}</h3>
							<div className="calendar-options">
								<a 
									href={googleCalUrl} 
									target="_blank" 
									rel="noopener noreferrer"
									className="calendar-btn google"
								>
									<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
										<path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 100-16 8 8 0 000 16zm1-8h4v2h-6V7h2v5z"/>
									</svg>
									Google Calendar
								</a>
								<a 
									href={outlookCalUrl} 
									target="_blank" 
									rel="noopener noreferrer"
									className="calendar-btn outlook"
								>
									<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
										<path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8zm1-13h-2v6h6v-2h-4z"/>
									</svg>
									Outlook
								</a>
								<button 
									onClick={() => downloadICS(calendarEvent)}
									className="calendar-btn apple"
								>
									<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
										<path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
									</svg>
									Apple / iCal
								</button>
							</div>
						</div>

						<div className="contact-info">
							<p>{t('form.weWillContact')}</p>
							<p>
								📞 {t('contact.phone')}: <strong>{CONTACT_INFO.phone}</strong>
							</p>
						</div>

						<Button variant="primary" onClick={() => window.location.href = '/'}>
							{t('common.backToHome', '返回首页')}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="book-now">
			<SEO title={t('nav.bookNow')} description={t('bookNow.subtitle')} />

			<div className="book-container">
				{/* Header */}
				<div className="book-header">
					<h1>{t('bookNow.title')}</h1>
					<p>{t('bookNow.selectRegion')}</p>
				</div>

				{/* Region Selection */}
				<section className="region-selection">
					<div className="region-grid">
						{REGIONS.map((region) => (
							<div
								key={region.id}
								id={region.id}
								className={`region-card ${selectedRegion === region.id ? 'selected' : ''}`}
								onClick={() => handleRegionSelect(region.id)}
							>
								<h3>{t(`nav.${region.id}`, region.name)}</h3>
								<p>{region.cities.slice(0, 8).join(', ')}...</p>
								<Button
									variant={selectedRegion === region.id ? 'primary' : 'outline'}
									size="sm"
								>
									{selectedRegion === region.id ? '✓' : t('form.selectOption')}
								</Button>
							</div>
						))}
					</div>
				</section>

				{/* Booking Form */}
				{selectedRegion && (
					<section className="booking-form-section" id="booking-form">
						<h2>{t('bookNow.formTitle')}</h2>
						<p className="form-subtitle">
							{t('form.region')}: <strong>{t(`nav.${selectedRegion}`, REGIONS.find((r) => r.id === selectedRegion)?.name)}</strong>
						</p>

						<div className="pricing-reminder">
							<span className="price">${PRICING.PER_PERSON}/{t('pricing.perPerson')}</span>
							<span className="minimum">${PRICING.MINIMUM_ORDER} {t('pricing.minimum')}</span>
						</div>

						<div className="booking-content">
							{/* Calendar */}
							<div className="calendar-sidebar">
								<h3>{t('form.date')} *</h3>
								<Calendar
									selectedDate={formData.date}
									onDateSelect={handleDateSelect}
									region={selectedRegion}
								/>
								{formData.date && (
									<p className="selected-date">
										✓ {new Date(formData.date + 'T00:00:00').toLocaleDateString(i18n.language, {
											weekday: 'long',
											year: 'numeric',
											month: 'long',
											day: 'numeric',
										})}
									</p>
								)}
							</div>

							{/* Form */}
							<form className="booking-form" onSubmit={handleSubmit}>
								<Input
									label={t('form.name')}
									name="name"
									value={formData.name}
									onChange={handleChange}
									placeholder={t('form.namePlaceholder') as string}
									required
								/>

								<div className="form-row">
									<Input
										label={t('form.email')}
										type="email"
										name="email"
										value={formData.email}
										onChange={handleChange}
										error={validationErrors.email}
										required
									/>

									<Input
										label={t('form.phone')}
										type="tel"
										name="phone"
										value={formData.phone}
										onChange={handleChange}
										error={validationErrors.phone}
										required
									/>
								</div>

								<Select
									label={t('form.time')}
									name="time"
									value={formData.time}
									onChange={handleChange}
									required
									options={[
										{ value: '', label: t('form.selectOption') as string },
										...timeSlots.map(slot => ({
											value: slot.id,
											label: `${t(`timeSlot.${slot.id}`, slot.label)} (${slot.startTime} - ${slot.endTime})`,
										})),
									]}
								/>

								<Input
									label={t('form.guests')}
									type="number"
									name="guestCount"
									value={formData.guestCount.toString()}
									onChange={handleChange}
									min={10}
									hint={`${t('form.guestsHint')} ($${PRICING.MINIMUM_ORDER})`}
									required
								/>

								<Textarea
									label={t('form.message')}
									name="message"
									value={formData.message}
									onChange={handleChange}
									placeholder={t('form.messagePlaceholder') as string}
									rows={3}
								/>

								{/* 推荐来源 */}
								<Select
									label={t('form.referralSource', '您是如何知道我们的？')}
									name="referralSource"
									value={formData.referralSource || ''}
									onChange={handleChange}
									options={[
										{ value: '', label: t('form.selectOption', 'Please select') as string },
										{ value: 'google', label: t('form.referral.google', 'Google Search') as string },
										{ value: 'instagram', label: 'Instagram' },
										{ value: 'facebook', label: 'Facebook' },
										{ value: 'tiktok', label: 'TikTok' },
										{ value: 'yelp', label: 'Yelp' },
										{ value: 'friend', label: t('form.referral.friend', 'Friend Referral') as string },
										{ value: 'event', label: t('form.referral.event', 'Seen at Event') as string },
										{ value: 'other', label: t('form.referral.other', 'Other') as string },
									]}
								/>

								{/* 优惠码 */}
								{couponsEnabled && (
									<>
										<div className="coupon-field">
											<Input
												label={t('form.couponCode', '优惠码')}
												name="couponCode"
												value={formData.couponCode || ''}
												onChange={(e) => {
													handleChange(e);
													setCouponStatus(null);
												}}
												placeholder={t('form.couponPlaceholder', '输入优惠码（可选）') as string}
											/>
											<button
												type="button"
												className="coupon-check-btn"
												onClick={() => validateCoupon(formData.couponCode || '')}
												disabled={checkingCoupon || !formData.couponCode?.trim()}
											>
												{checkingCoupon ? '...' : t('form.applyCoupon', '验证')}
											</button>
										</div>
										{couponStatus && (
											<div className={`coupon-status ${couponStatus.valid ? 'valid' : 'invalid'}`}>
												{couponStatus.valid ? '✅' : '❌'} {couponStatus.message}
											</div>
										)}
									</>
								)}

								{/* Hidden date field */}
								<input type="hidden" name="date" value={formData.date} />

								{!formData.date && (
									<div className="form-hint">← {t('form.date')}</div>
								)}

								{submitStatus === 'error' && (
									<div className="form-error">{errorMessage || t('form.error')}</div>
								)}

								<Button
									type="submit"
									variant="primary"
									size="lg"
									loading={isSubmitting}
									disabled={!formData.date || !!validationErrors.email || !!validationErrors.phone}
									className="submit-btn"
								>
									{isSubmitting ? t('form.submitting') : t('form.submitReservation')}
								</Button>

								<p className="no-payment-note">
									{t('form.noPaymentRequired')}
								</p>
							</form>
						</div>
					</section>
				)}

				{/* Contact Info */}
				<section className="booking-contact">
					<h3>{t('contact.title')}</h3>
					<p>
						{t('contact.phone')}: <strong>{CONTACT_INFO.phone}</strong>
					</p>
					<p>
						{t('contact.email')}: <strong>{CONTACT_INFO.email}</strong>
					</p>
				</section>
			</div>
		</div>
	);
};

export default BookNow;
